// Settings (29 Oct 2026, docs/specs/NAVIGATION_2026_10.md): every choice about how Ogma behaves, in nine sections (Campaign defaults moved to Campaigns, Automations joined, 30 Oct 2026).
// Each section shows what most owners change; its Advanced switch shows the rest (`advanced` pages). Find's and
// Connect's accounts and rules, Settings and the old Settings all moved here; the paused modules' parts sit under
// Modules. Guided set-up (/setup) belongs here too.
// Screens are loaded with screen() (see ./screen.ts), never imported directly.
import type { SteppedModule } from "./types";
import { screen } from "./screen";

// Your business
const OfferSection = screen(() => import("../screens/company/OfferSection"), "OfferSection");
const SectorPage = screen(() => import("../screens/settings/SectorPage"), "SectorPage");
// 9.13 every reply path at once (moved from Campaigns › Shared set-ups, 31 Oct 2026: each campaign sets its own steps)
const ReplyPaths = screen(() => import("../screens/pipeline/ConnectPages"), "PathsRules");
const BrandPage = screen(() => import("../screens/settings/BrandPage"), "BrandPage");
const PlaybookSection = screen(() => import("../screens/company/PlaybookSection"), "PlaybookSection");
const PlaysPage = screen(() => import("../screens/setup/Plays"), "PlaysPage");
// Sending
const MailboxesPage = screen(() => import("../screens/find/Mailboxes"), "MailboxesPage");
const SendingRulesPage = screen(() => import("../screens/find/SendingRules"), "SendingRulesPage");
const SendMorePage = screen(() => import("../screens/find/SendMore"), "SendMorePage");
const FindHealthPage = screen(() => import("../screens/find/Health"), "HealthPage");
// Meetings and Connect's accounts (Campaign defaults moved to Campaigns › Defaults, 30 Oct 2026)
const connectPages = () => import("../screens/pipeline/ConnectPages");
const CalendarAccount = screen(connectPages, "CalendarAccount");
const BookingHoursPage = screen(() => import("../screens/pipeline/BookingHours"), "BookingHoursPage");
const MeetingTypesRules = screen(connectPages, "MeetingTypesRules");
const BookingLinksAccount = screen(connectPages, "BookingLinksAccount");
const VideoCallToolAccount = screen(() => import("../screens/pipeline/ConnectAccounts"), "VideoCallToolAccount");
const NotetakerAccount = screen(() => import("../screens/pipeline/NotetakerAccount"), "NotetakerAccount");
// Approvals
const sections = () => import("../screens/settings/Sections");
const ApprovalSettings = screen(sections, "ApprovalSettings");
const ApprovalsSpendPage = screen(() => import("../screens/find/ApprovalsSpend"), "ApprovalsSpendPage");
const ConnectApprovals = screen(connectPages, "ApprovalsRules");
// Data
const YourContacts = screen(() => import("../screens/find/YourContacts"), "YourContacts");
const Exports = screen(() => import("../screens/Exports"), "Exports");
const Duplicates = screen(() => import("../screens/Duplicates"), "Duplicates");
const CrmSettings = screen(() => import("../screens/CrmSettings"), "CrmSettings");
const CrmLinkPage = screen(() => import("../screens/find/CrmLink"), "CrmLinkPage");
const DataSourcesPage = screen(() => import("../screens/find/DataSources"), "DataSourcesPage");
// Connections
const Connections = screen(sections, "Connections");
const AccountsOverview = screen(() => import("../screens/pipeline/ConnectAccounts"), "AccountsOverview");
const PaymentsAccount = screen(() => import("../screens/pipeline/PaymentsAccount"), "PaymentsAccount");
// Modules
const YourModules = screen(() => import("../screens/company/YourModules"), "YourModules");
const WhatsAppNumberPage = screen(connectPages, "WhatsAppAccountPage");
const WhatsAppRules = screen(connectPages, "WhatsAppRules");
const WhatsAppOutreachPage = screen(() => import("../screens/find/WhatsAppAccount"), "WhatsAppAccountPage");
const PhoneAccountsPage = screen(() => import("../screens/find/Phone"), "PhoneAccountsPage");
const PhoneLineAccount = screen(() => import("../screens/pipeline/PhoneLineAccount"), "PhoneLineAccount");
const CallsPage = screen(() => import("../screens/find/CallsToMake"), "CallsPage");
const LettersAccountPage = screen(() => import("../screens/find/LettersAccount"), "LettersAccountPage");
const LinkedInAccountPage = screen(() => import("../screens/find/LinkedInAccount"), "LinkedInAccountPage");
const Setup = screen(() => import("../screens/setup/Setup"), "Setup");
// Account
const Profile = screen(sections, "Profile");
const Team = screen(() => import("../screens/Settings"), "Team");
const PersonalSettings = screen(sections, "PersonalSettings");
const Automations = screen(() => import("../screens/settings/Automations"), "Automations");
const DangerZone = screen(sections, "DangerZone");

export const settings: SteppedModule = {
  key: "settings",
  label: "Settings",
  layout: "steps",
  tabs: [
    {
      key: "business",
      label: "Your business",
      pages: [
        { slug: "", label: "What you sell", step: "2.30", render: () => <OfferSection /> },
        { slug: "sector", label: "Sector and defaults", step: "0.36", render: () => <SectorPage /> },
        { slug: "brand", label: "Brand", step: "0.13", advanced: true, render: () => <BrandPage /> },
        { slug: "playbook", label: "Playbook", step: "0.5", advanced: true, render: () => <PlaybookSection /> },
        { slug: "plays", label: "Plays", step: "0.5", advanced: true, render: () => <PlaysPage /> },
        { slug: "paths", label: "Reply paths", step: "9.13", advanced: true, render: () => <ReplyPaths /> },
      ],
    },
    {
      key: "sending",
      label: "Sending",
      pages: [
        { slug: "", label: "Mailboxes", step: "4.3", render: () => <MailboxesPage /> },
        { slug: "times", label: "Send times and limits", step: "4.3", render: () => <SendingRulesPage /> },
        { slug: "more", label: "Send more each day", step: "4.3", advanced: true, render: () => <SendMorePage /> },
        { slug: "health", label: "Mailbox and search health", step: "2.39", advanced: true, render: () => <FindHealthPage /> },
      ],
    },
    {
      key: "meetings",
      label: "Meetings",
      pages: [
        { slug: "", label: "Calendar", step: "7.4", render: () => <CalendarAccount /> },
        { slug: "hours", label: "Booking hours", step: "7.4", render: () => <BookingHoursPage /> },
        { slug: "types", label: "Meeting types and reminders", step: "7.8", advanced: true, render: () => <MeetingTypesRules /> },
        { slug: "links", label: "Booking links", step: "7.9", advanced: true, render: () => <BookingLinksAccount /> },
        { slug: "video", label: "Video call tool", step: "7.4", advanced: true, render: () => <VideoCallToolAccount /> },
        { slug: "notetaker", label: "Meeting notetaker", step: "8.8", advanced: true, render: () => <NotetakerAccount /> },
      ],
    },
    {
      key: "approvals",
      label: "Approvals",
      pages: [
        { slug: "", label: "Your approvals", step: "E13", render: () => <ApprovalSettings /> },
        { slug: "outreach", label: "Outreach approvals", step: "E13", advanced: true, render: () => <ApprovalsSpendPage /> },
        { slug: "conversations", label: "Conversation approvals", step: "E13", advanced: true, render: () => <ConnectApprovals /> },
      ],
    },
    {
      key: "data",
      label: "Data",
      pages: [
        { slug: "", label: "Import contacts", step: "0.28", render: () => <YourContacts /> },
        { slug: "exports", label: "Exports", render: () => <Exports /> },
        { slug: "duplicates", label: "Duplicates", step: "3.15", advanced: true, render: () => <Duplicates /> },
        { slug: "crm", label: "Tags, stages and sources", step: "3.10", advanced: true, render: () => <CrmSettings /> },
        { slug: "crm-link", label: "Contacts you already know", step: "0.28", advanced: true, render: () => <CrmLinkPage /> },
        { slug: "sources", label: "Data sources", step: "2.1", advanced: true, render: () => <DataSourcesPage /> },
      ],
    },
    {
      key: "connections",
      label: "Connections",
      pages: [
        { slug: "", label: "Connections", step: "0.8", render: () => <Connections /> },
        { slug: "sales", label: "Sales accounts", step: "0.49", advanced: true, render: () => <AccountsOverview /> },
        { slug: "payments", label: "Payments", step: "9.20", advanced: true, render: () => <PaymentsAccount /> },
        {
          slug: "documents",
          label: "Documents",
          step: "9.6",
          advanced: true,
          comingSoon: "Connect an e-signing service here, so proposals and contracts you approve go out to sign and come back filed against the deal.",
        },
      ],
    },
    {
      // Craig, 30 Oct 2026: everything Ogma runs by itself, whether it works and its one-click fix (replaces What's
      // running and Engine checks)
      key: "automations",
      label: "Automations",
      pages: [{ slug: "", label: "Automations", step: "0.24", render: () => <Automations /> }],
    },
    {
      key: "modules",
      label: "Modules",
      pages: [
        { slug: "", label: "Modules", render: () => <YourModules /> },
        { slug: "whatsapp", label: "WhatsApp number", step: "0.30", advanced: true, render: () => <WhatsAppNumberPage /> },
        { slug: "whatsapp-templates", label: "WhatsApp templates", step: "0.31", advanced: true, render: () => <WhatsAppRules /> },
        { slug: "whatsapp-outreach", label: "WhatsApp for outreach", step: "4.9", advanced: true, render: () => <WhatsAppOutreachPage /> },
        { slug: "phone", label: "Phone", step: "4.13", advanced: true, render: () => <PhoneAccountsPage /> },
        { slug: "phone-line", label: "Phone line", step: "5.13", advanced: true, render: () => <PhoneLineAccount /> },
        { slug: "calls", label: "Calls to make", step: "4.13", part: "find.calls", advanced: true, render: () => <CallsPage /> },
        { slug: "letters", label: "Letters", step: "4.4", advanced: true, render: () => <LettersAccountPage /> },
        { slug: "linkedin", label: "LinkedIn", step: "3.5", advanced: true, render: () => <LinkedInAccountPage /> },
      ],
    },
    {
      key: "account",
      label: "Account",
      pages: [
        { slug: "", label: "Profile", render: () => <Profile /> },
        { slug: "team", label: "Team", render: () => <Team /> },
        { slug: "personal", label: "Personal settings", advanced: true, render: () => <PersonalSettings /> },
        { slug: "danger", label: "Danger zone", step: "0.44", advanced: true, render: () => <DangerZone /> },
      ],
    },
  ],
  // Guided set-up after going live: "Change what Ogma runs" and a play for each further module.
  extraPages: [
    { path: "/setup", label: "What Ogma runs", render: () => <Setup /> },
    { path: "/setup/:step", label: "What Ogma runs", render: () => <Setup /> },
    { path: "/setup/:step/:module", label: "What Ogma runs", render: () => <Setup /> },
  ],
  taskHomes: {
    approve_config: ["/settings/business/playbook"],
    contact_finder_problem: ["/settings/data/sources"],
    review_duplicates: ["/settings/data/duplicates"],
    // 2.39, 10.20 a search running low or its replies dropping: Sending's health page shows both
    search_running_low: ["/settings/sending/health"],
    search_replies_dropped: ["/settings/sending/health"],
    call_back: ["/settings/modules/calls"],
    approve_whatsapp_templates: ["/settings/modules/whatsapp-templates"],
    whatsapp_template_problem: ["/settings/modules/whatsapp-templates"],
    reconnect_phone_line: ["/settings/modules/phone-line"],
    notetaker_problem: ["/settings/meetings/notetaker"],
    payment_problem: ["/settings/connections/payments"],
    autonomy_promotion: ["/settings/approvals"],
    autonomy_demoted: ["/settings/approvals"],
    budget_reached: ["/settings/automations"],
    review_quality_failure: ["/settings/approvals"],
    // Connect package 4 (E13): the weekly review proposes a campaign's step one level up
    autonomy_scope_promotion: ["/settings/approvals/conversations"],
    engine_part_failing: ["/settings/automations"],
    health_alert: ["/settings/automations"],
    // 2.38 scoring paused for lack of credits: Ogma's team tops them up (the central workspace only)
    find_scoring_blocked: ["/settings/automations"],
  },
};
